import { Box, Flex } from '@components/layout/Grid';
import { useRelatedVideosQuery, useVideoClickMutation } from '@gql/generated';
import LanguageContext from '@lib/contexts/languageContext';
import { Trans } from '@lingui/react';
import { useRouter } from 'next/router';
import { memo, useContext, useState } from 'react';
import RelatedVideoCard from './cards/RelatedVideoCard';
import { REF_BUNDLE } from '@lib/constants';
import { VideoBundleBanner } from './VideoBundleBanner';
import { H4 } from '@components/typography/Headings';
import { RelatedVideoCardSkeletonV2 } from '@skeletons/RelatedVideoCardSkeleton';
import styled from 'styled-components';
import { items5 } from '@lib/faker';
import { Button } from '@components/ButtonV2';

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > *:not(:last-child) {
    margin-bottom: 10px;
  }

  margin-bottom: 20px;
`;

type RelatedVideosProps = {
  id: string;
};

export const RelatedVideos = memo(({ id }: RelatedVideosProps) => {
  const lang = useContext(LanguageContext);
  const router = useRouter();
  const { ref } = router.query;

  const [limit, setLimit] = useState(5);
  const [videoClick] = useVideoClickMutation();

  const handleRelatedVideoClick = ({ id, lang }) => {
    router.push(`/${lang}/videos/${id}`);
  };

  const { loading: loadingRelatedVideos, error, data } = useRelatedVideosQuery({
    variables: { id },
    ssr: false,
  });

  if (loadingRelatedVideos)
    return (
      <>
        {items5.map((item, index) => (
          <RelatedVideoCardSkeletonV2 key={index} />
        ))}
      </>
    );

  if (error || !data?.relatedVideos?.length) {
    return null;
  }

  const { relatedVideos } = data;

  return (
    <>
      <Box mb="20px" mt="0px" as={H4}>
        <Trans id="video.related_video_title" message="Similar sessions" />
      </Box>
      {ref === REF_BUNDLE ? <VideoBundleBanner /> : null}
      <ListWrapper>
        {relatedVideos.slice(0, limit).map(item => (
          <RelatedVideoCard
            key={item.id}
            name={item.content.title}
            onClick={() => {
              videoClick({
                variables: {
                  videoId: item.id,
                  sourceId: id,
                  sourceType: 'video',
                  recommended: !!item.recommended,
                },
              });
              handleRelatedVideoClick({
                id: item.identifier,
                lang,
              });
            }}
            description={item.content.shortDescription}
            thumbnailUrl={item.assets.thumbnailUrlMedium}
            duration={item.meta.minutes}
            cursorAt={item.cursorAt}
            identifier={item.identifier}
            id={item.id}
          />
        ))}
        {relatedVideos?.length > limit ? (
          <Flex justifyContent="center" mt={10}>
            <Button outline onClick={() => setLimit(limit + 5)} size="small">
              Show more
            </Button>
          </Flex>
        ) : null}
      </ListWrapper>
    </>
  );
});
